<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import Pagination from '../../Pagination.svelte';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import {
    adminDashboardService,
    type RecentPendingConfirmationCharity
  } from '../../../lib/api/admin/dashboard.service';
  import { createPreferencesManager } from '../../../lib/utils/preferences';
  import { formatDate } from '../../../lib/utils/formatters';

  const preferences = createPreferencesManager('pending-confirmation');

  let loading = true;
  let error: string | null = null;
  let charities: RecentPendingConfirmationCharity[] = [];
  let totalItems = 0;
  let totalPages = 1;

  const savedFilters = preferences.getFilters();
  let searchQuery = savedFilters.searchQuery || '';
  let currentPage = preferences.getCurrentPage() || 1;
  const itemsPerPage = 20;

  const tableColumns = [
    {
      key: 'charity_name',
      label: 'Charity',
      width: '34%',
      mobilePrimary: true,
      render: (value: string) => `<span class="charity-name">${value}</span>`
    },
    {
      key: 'payout_count',
      label: 'Payments',
      width: '18%',
      render: (value: number) => `<span class="count-value">${value}</span>`
    },
    {
      key: 'total_amount',
      label: 'Total paid',
      width: '24%',
      render: (value: number) =>
        `<span class="amount-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'latest_paid_at',
      label: 'Latest payout',
      width: '24%',
      render: (value: string | null) =>
        `<span class="date-value">${value ? formatDate(value) : '—'}</span>`
    }
  ];

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  async function loadCharities() {
    loading = true;
    error = null;

    try {
      const response = await adminDashboardService.getPendingConfirmationCharities({
        page: currentPage,
        per_page: itemsPerPage,
        phrase: searchQuery || undefined
      });

      if (response.success && response.data) {
        charities = response.data.items;
        totalItems = response.data.paging.total;
        totalPages = response.data.paging.last_page;
      } else {
        error = response.message || 'Failed to load charities';
      }
    } catch (err: unknown) {
      error = err instanceof Error ? err.message : 'Failed to load charities';
    } finally {
      loading = false;
    }
  }

  function handleSearch(event: CustomEvent<string>) {
    searchQuery = event.detail;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery } });
    loadCharities();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    preferences.update({ currentPage });
    loadCharities();
  }

  function handleRowClick(row: RecentPendingConfirmationCharity) {
    if (row?.charity_uuid) {
      push(
        `/charity-profile/${row.charity_uuid}?return=${encodeURIComponent('/admin/pending-confirmation')}`
      );
    }
  }

  function clearSearch() {
    searchQuery = '';
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery: '' } });
    loadCharities();
  }

  onMount(() => {
    loadCharities();
  });
</script>

<div class="payments-list-page">
  <div class="page-header">
    <p class="subtitle">Charities that have been paid but have not yet confirmed receipt of funds in their bank account.</p>
  </div>

  <div class="filters-section">
    <SearchInput
      placeholder="Search charities..."
      bind:value={searchQuery}
      on:search={handleSearch}
      width="100%"
    />
  </div>

  <div class="table-section">
    {#if loading}
      <div class="loading-wrap">
        <LoadingSkeleton lines={8} height="2.5rem" />
      </div>
    {:else if error}
      <ErrorState
        message="Unable to load charities"
        description={error}
        onRetry={loadCharities}
      />
    {:else if charities.length === 0}
      <EmptyState
        message="No charities awaiting confirmation"
        description={searchQuery
          ? 'Try adjusting your search to see more results.'
          : 'All paid charities have confirmed receipt of their payouts.'}
        actionLabel={searchQuery ? 'Clear search' : null}
        onAction={searchQuery ? clearSearch : null}
      />
    {:else}
      <Table columns={tableColumns} data={charities} onRowClick={handleRowClick} />
      {#if totalPages > 1}
        <div class="pagination-container">
          <Pagination {currentPage} {totalPages} on:pageChange={handlePageChange} />
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .page-header {
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .filters-section {
    margin-bottom: 1rem;
    width: 100%;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .loading-wrap {
    padding: 2rem;
    min-height: 320px;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }

  :global(.amount-value) {
    font-weight: 600;
    color: #111827;
  }
</style>
